import { FC, useCallback, useEffect, useState } from 'react';

// API
import apibridge from '../../../apibridge';

// REDUX
import { ToastMessagesSlice } from '../../../store/slice/ToastMessages';
import { useDispatch } from 'react-redux';

// PACKAGES
import { useNavigate } from 'react-router-dom';
import { Modal, Offcanvas, Spinner } from 'react-bootstrap';

// COMPONENTS
import SvgMask from '../../../components/_Helpers/SvgMask';
import DelayedFadeIn from '../../../components/_Helpers/DelayedFadeIn';

// UTILS
import { guid } from '../../../libs/utils';

// TYPES
import {
	AssessmentClassesClass,
	AssessmentStudentNavigationListStudent,
	AssessmentSubType,
	AssessmentType
} from '../../../api/models';

interface StudentNavigationSpellingProps {
	showOffCanvas: boolean;
	onHideOffCanvas: () => void;
	showConfirmationBar: boolean;
	onConfirmEditAssessment: () => Promise<void>;
	onDiscardChanges: () => void;
	assessmentName?: string;
	semesterLabel?: string;
	assessmentId?: string;
	assessmentPeriodId?: string;
	classId?: string;
	studentId?: string;
	type?: AssessmentSubType;
}

const StudentNavigationSpelling: FC<StudentNavigationSpellingProps> = ({
	showOffCanvas,
	onHideOffCanvas,
	showConfirmationBar,
	onConfirmEditAssessment,
	onDiscardChanges,
	assessmentName,
	semesterLabel,
	assessmentId,
	assessmentPeriodId,
	classId,
	studentId,
	type
}) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	// Get Details from props (passed from parent component)
	const assessmentType = type || undefined;

	// State to store the class list
	const [classList, setClassList] = useState<AssessmentClassesClass[]>([]);
	const [selectedClassId, setSelectedClassId] = useState<string | undefined>(classId);

	// State to store current student
	const [selectedStudent, setSelectedStudent] = useState<AssessmentStudentNavigationListStudent | undefined>(undefined);

	// State to store the student navigation list
	const [studentNavigationList, setStudentNavigationList] = useState<AssessmentStudentNavigationListStudent[]>([]);

	// State to show/hide Modal
	const [showStudentNavigationConfirmModal, setShowStudentNavigationConfirmModal] = useState(false);

	// Loading state
	const [isLoading, setIsLoading] = useState(false);

	// Function to get Class List
	const getAssessmentClassesData = useCallback(async () => {
		setIsLoading(true);
		const response = await apibridge.getAssessmentClasses();

		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				setClassList(response.data.result?.classes || []);
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Class List error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}
		setIsLoading(false);
	}, [dispatch]);

	useEffect(() => {
		getAssessmentClassesData();
	}, [getAssessmentClassesData]);

	// Set selectedClassId when classId prop changes
	useEffect(() => {
		if (classId) {
			setSelectedClassId(classId);
		}
	}, [classId]);

	// Function to get the student navigation list
	const getAssessmentStudentNavigationList = async (classIdToUse?: string) => {
		setIsLoading(true);
		const response = await apibridge.getAssessmentStudentNavigationList(
			studentId,
			classIdToUse || selectedClassId || classId,
			assessmentId,
			assessmentPeriodId,
			assessmentType
		);

		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				setStudentNavigationList(response.data.result?.students || []);
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Student List error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			} else {
				// No result means no students for this class, clear the list
				setStudentNavigationList([]);
			}
		}
		setIsLoading(false);
	};

	useEffect(() => {
		if (studentId && classId && assessmentId && assessmentPeriodId && assessmentType) {
			getAssessmentStudentNavigationList();
		}
	}, [studentId, classId, assessmentId, assessmentPeriodId, assessmentType]); // eslint-disable-line react-hooks/exhaustive-deps

	// Watch for selectedClassId changes and re-run the student navigation list
	useEffect(() => {
		if (studentId && selectedClassId && assessmentId && assessmentPeriodId && assessmentType) {
			getAssessmentStudentNavigationList(selectedClassId);
		}
	}, [selectedClassId]); // eslint-disable-line react-hooks/exhaustive-deps

	// Confirm if there is UNSAVED change or not
	const confirmStudentNavigation = (student: AssessmentStudentNavigationListStudent) => {
		// If the confirmation bar is showing, that means user have unsaved change, open up the Modal
		if (showConfirmationBar) {
			setShowStudentNavigationConfirmModal(true);
			setSelectedStudent(student);
		} else {
			// If the confirmation bar is NOT showing, that means there is NO unsaved change
			proceedStudentNavigation(student);
		}
	};

	// Proceed with the student navigation
	const proceedStudentNavigation = async (student?: AssessmentStudentNavigationListStudent) => {
		if (!student?.id) return;

		onHideOffCanvas();

		// If the student has a recordId, navigate to the existing assessment
		if (student.recordId) {
			navigate(
				`/assessment-management/${'spelling'}?recordId=${student.recordId}&assessmentName=${encodeURIComponent(
					assessmentName || ''
				)}`
			);
		} else {
			setIsLoading(true);
			// If the student does not have a recordId, create a new assessment
			const submitData = {
				type: 'Llass' as AssessmentType,
				assessmentId: assessmentId,
				studentId: student.id,
				assessmentPeriodId: assessmentPeriodId
			};

			const response = await apibridge.postAssessmentCreate(submitData);

			if (response && response.data) {
				if (!response.data.isError && response.data.result) {
					const recordId = response.data.result.id;

					navigate(
						`/assessment-management/${'spelling'}?recordId=${recordId}&assessmentName=${encodeURIComponent(
							assessmentName || ''
						)}`
					);
				} else if (response.data.validationErrors) {
					for (const err of response.data.validationErrors) {
						dispatch(
							ToastMessagesSlice.actions.add({
								id: guid(),
								type: 'danger',
								heading: 'Create Assessment error',
								description: err.reason || 'Unknown error'
							})
						);
					}
				}
			}

			setIsLoading(false);
		}
	};

	return (
		<>
			{/* STUDENT NAVIGATION OFF CANVAS */}
			<Offcanvas
				bsPrefix="view-teacher offcanvas"
				show={showOffCanvas}
				onHide={onHideOffCanvas}
				onExited={() => {}}
				placement="end"
			>
				<Offcanvas.Header closeButton>
					<Offcanvas.Title className="d-flex flex-column justify-content-center align-items-center w-100 gap-1">
						<div className="d-flex align-items-center gap-2">
							<h2 className="text-shades-800 mb-2">
								<strong>View a different student</strong>
							</h2>
						</div>

						<p className="h4 text-dark-text mb-0">LLASS {assessmentName}</p>

						<p className="h4 text-dark-text mb-0">{semesterLabel}</p>
					</Offcanvas.Title>
				</Offcanvas.Header>

				<Offcanvas.Body className="d-flex flex-column">
					{isLoading ? (
						<div className="d-flex align-items-center justify-content-center w-100">
							<DelayedFadeIn>
								<Spinner animation="border" role="status">
									<span className="visually-hidden">Loading...</span>
								</Spinner>
							</DelayedFadeIn>
						</div>
					) : (
						<>
							<div className="d-flex justify-content-between gap-3 mb-4">
								<div className="form-group w-100">
									<label htmlFor="className" className="body-small mb-1">
										<strong>Class</strong>
									</label>

									<select
										value={selectedClassId?.toLowerCase() || ''}
										onChange={(e) => {
											setSelectedClassId(e.target.value);
										}}
										className="form-select"
										name="classDetail"
										id="classDetail"
									>
										<option value="" disabled>
											Please select...
										</option>

										{classList?.length &&
											classList.map((classListItem, classListIndex) => {
												return (
													<option value={classListItem.id?.toLowerCase()} key={classListItem.id || classListIndex}>
														{classListItem.name}
													</option>
												);
											})}
									</select>
								</div>
							</div>

							<>
								<div className="form-selection-wrapper mb-0">
									<div className="form-selection-wrapper-inner">
										{studentNavigationList?.length ? (
											studentNavigationList.map((studentItem, studentIndex) => {
												return (
													<button
														className={`btn btn-row-label ${studentItem.currentStudent ? 'current-view-student' : ''}`}
														key={studentItem.id || studentIndex}
														onClick={() => {
															confirmStudentNavigation(studentItem);
														}}
													>
														<div className="d-flex gap-2">
															<strong>
																{studentItem.firstName} {studentItem.lastName}
															</strong>
														</div>

														<div className="d-flex align-items-center gap-2">
															{studentItem.currentStudent ? (
																<span className="body-small text-shades-500">
																	<strong>Currently viewing</strong>
																</span>
															) : studentItem.recordId ? (
																<span className="body-small">
																	<strong>Previously marked</strong>
																</span>
															) : (
																<span className="body-small status-text">
																	<strong>Not assessed</strong>
																</span>
															)}

															<div className="selected-icon-wrapper">
																<SvgMask path="/svg/chevron-right.svg" width={16} height={16} />
															</div>
														</div>
													</button>
												);
											})
										) : (
											<p className="text-shades-500 text-center mt-5 body-large">
												There is currently no student in this class.
											</p>
										)}
									</div>
								</div>
							</>
						</>
					)}
				</Offcanvas.Body>
			</Offcanvas>

			{/* STUDENT NAVIGATION CONFIRM MODAL */}
			<Modal
				show={showStudentNavigationConfirmModal}
				onHide={() => setShowStudentNavigationConfirmModal(false)}
				centered
			>
				<Modal.Body>
					<h2 className="m-0 text-shades-800">
						<strong>Unsaved changes on current record</strong>
					</h2>

					<p className="m-0">You have unsaved changes on the record you are currently viewing. </p>

					<p>How would you like to continue?</p>

					<div className="d-flex flex-column gap-2">
						<button
							onClick={async () => {
								await onConfirmEditAssessment();
								proceedStudentNavigation(selectedStudent);
								setShowStudentNavigationConfirmModal(false);
							}}
							className="btn btn-lg w-100"
						>
							<SvgMask path={'/svg/save-icon.svg'} width={16} height={16} />
							Save and continue
						</button>

						<button
							onClick={() => {
								onDiscardChanges();
								proceedStudentNavigation(selectedStudent);
								setShowStudentNavigationConfirmModal(false);
							}}
							className="btn btn-exit btn-lg w-100"
						>
							<SvgMask path={'/svg/trash-icon.svg'} width={16} height={16} /> Continue without saving
						</button>
					</div>

					<button
						onClick={() => {
							setShowStudentNavigationConfirmModal(false);
						}}
						className="btn btn-white btn-lg w-100 mt-3"
					>
						Cancel
					</button>
				</Modal.Body>
			</Modal>
		</>
	);
};

export default StudentNavigationSpelling;
